import { createContext } from 'react';
import utils from 'utils';
import {
  DataRoomNodeAccessLevel,
  Lead,
  LeadStages,
  LeadState,
} from 'models/Lead';
import { LeadDrawerOptions } from '../../../../models';

export type ReleaseFileUndoInstance = {
  leadId: string;
  dataRoomNodeId: string;
  accessLevel: DataRoomNodeAccessLevel;
  name: string;
};

type ListingContextProps = {
  hasAnyLeads: boolean;
  selectedLead: Lead;
  selectedLeadDrawer: LeadDrawerOptions;
  showInactiveLeads: boolean;
  totalUnreadChatsCount: number;
  totalUnrepliedChatsCount: number;
  showFileAccessSettings: boolean;
  openReorderFilesDrawer: () => void;
  updateLead: (newLead: Lead, isChangingActivity?: boolean) => void;
  updateLeads: (lead: Lead[]) => void;
  removeLead: (leadId: string) => void;
  setSelectedLead: (val: Lead | null) => void;
  setSelectedLeadDrawer: (value: LeadDrawerOptions) => void;
  changeState: (
    lead: Lead,
    newState: LeadState,
    startIndex: number,
    recursiveCall?: boolean,
    revokeDataRoomAccess?: boolean,
  ) => Promise<void>;
  checkIsLeadUpdating: (id: string) => boolean;
  openLeadDrawer: (lead: Lead, tab?: LeadDrawerOptions) => void;
  closeLeadDrawer: () => void;
  openWithdrawModal: (lead: Lead) => void;
  setShowInactiveLeads: React.Dispatch<React.SetStateAction<boolean>>;
  isUndoingReleaseFileForNode: (dataRoomNodeId: string) => boolean;
  showReleaseFileUndo?: (file: ReleaseFileUndoInstance) => void;
  refetchLead: (leadId: string) => void;

  //grid
  isLoadingGrid: boolean;
  numberOfOnGoingGridLeads: number;
  numberOfWaitlistedGridLeads: number;
  numberOfWaitlistedGridUnreadMessagesNotes: number;
  numberOfWaitlistedGridUnrepliedMessagesNotes: number;
  gridHasAnyLeads: boolean;
  gridLeads: Record<LeadState, Lead[]>;
  waitlistedLeads: Record<LeadState, Lead[]>;
  ongoingLeads: Record<LeadState, Lead[]>;
  scrollableContainer: HTMLElement;
  gridSort: Record<LeadStages, string>;
  onGridSortStep: (step: LeadStages, sort: string) => void;
  scrollableContainerRef: (element: HTMLElement) => void;

  //table
  isLoadingTable: boolean;
  totalOngoingPages: number;
  onGoingTableLeads: Lead[];
  totalWaitlistedPages: number;
  waitlistedTableLeads: Lead[];
  isLoadingWaitlistedTable: boolean;
  isLoadingOngoingTable: boolean;
  tableHasAnyLeads: boolean;
  refetch: () => void;
};

export default createContext<ListingContextProps>({
  numberOfOnGoingGridLeads: 0,
  numberOfWaitlistedGridLeads: 0,
  numberOfWaitlistedGridUnreadMessagesNotes: 0,
  numberOfWaitlistedGridUnrepliedMessagesNotes: 0,
  totalUnreadChatsCount: 0,
  totalUnrepliedChatsCount: 0,
  showFileAccessSettings: false,
  hasAnyLeads: false,
  selectedLeadDrawer: null,
  selectedLead: null,
  showInactiveLeads: false,
  checkIsLeadUpdating: utils.noop,
  setSelectedLeadDrawer: utils.noop,
  changeState: utils.noop,
  updateLead: utils.noop,
  updateLeads: utils.noop,
  removeLead: utils.noop,
  setSelectedLead: utils.noop,
  openLeadDrawer: utils.noop,
  closeLeadDrawer: utils.noop,
  openWithdrawModal: utils.noop,
  setShowInactiveLeads: utils.noop,
  isUndoingReleaseFileForNode: () => false,
  showReleaseFileUndo: utils.noop,
  openReorderFilesDrawer: utils.noop,
  refetchLead: utils.noop,

  //grid
  isLoadingGrid: false,
  gridHasAnyLeads: false,
  gridLeads: null,
  waitlistedLeads: null,
  ongoingLeads: null,
  scrollableContainer: null,
  gridSort: null,
  onGridSortStep: utils.noop,
  scrollableContainerRef: utils.noop,

  //table
  isLoadingTable: false,
  tableHasAnyLeads: false,
  isLoadingWaitlistedTable: false,
  isLoadingOngoingTable: false,
  totalOngoingPages: 0,
  onGoingTableLeads: [],
  totalWaitlistedPages: 0,
  waitlistedTableLeads: [],
  refetch: utils.noop,
});
